/**
 * Admin edit/create form for ManagedItem.
 * :id present → edit mode; absent → create mode.
 */

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { adminCreate, adminUpdate, adminList, type AdminItemBody } from "@/api";
import { useI18n } from "@/i18n";
import BackBar from "@/components/BackBar";

const KINDS = ["events", "exclusive", "library", "news", "reports"] as const;
const LOCALES = ["uk", "en"] as const;

type KindType = (typeof KINDS)[number];
type LocaleType = (typeof LOCALES)[number];

interface FormState {
  kind: KindType;
  locale: LocaleType;
  title: string;
  subtitle: string;
  paragraphsText: string; // blank-line-separated in UI
  image_url: string;
  link: string;
  date: string;
  sort: string;
  published: boolean;
}

const EMPTY_FORM: FormState = {
  kind: "news",
  locale: "uk",
  title: "",
  subtitle: "",
  paragraphsText: "",
  image_url: "",
  link: "",
  date: "",
  sort: "",
  published: false,
};

function formToBody(form: FormState): AdminItemBody {
  const body: AdminItemBody = {
    kind: form.kind,
    locale: form.locale,
    title: form.title.trim(),
    published: form.published,
  };

  const subtitle = form.subtitle.trim();
  if (subtitle) body.subtitle = subtitle;

  const paragraphs = form.paragraphsText
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (paragraphs.length > 0) body.paragraphs = paragraphs;

  const image_url = form.image_url.trim();
  if (image_url) body.image_url = image_url;

  const link = form.link.trim();
  if (link) body.link = link;

  if (form.date) body.date = form.date;

  if (form.sort !== "") body.sort = Number(form.sort);

  return body;
}

export default function AdminEdit() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const isCreate = !id;

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [loading, setLoading] = useState(!isCreate);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);

  // Load existing item when editing
  useEffect(() => {
    if (!id) return;
    adminList()
      .then((items) => {
        const found = items.find((i) => i.id === id);
        if (found) {
          const kind = (KINDS.includes(found.kind as KindType) ? found.kind : "news") as KindType;
          const locale = (LOCALES.includes(found.locale as LocaleType) ? found.locale : "uk") as LocaleType;
          setForm({
            kind,
            locale,
            title: found.title,
            subtitle: found.subtitle ?? "",
            paragraphsText: (found.paragraphs ?? []).join("\n\n"),
            image_url: found.image_url ?? "",
            link: found.link ?? "",
            date: found.date ?? "",
            sort: found.sort !== undefined ? String(found.sort) : "",
            published: found.published ?? false,
          });
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function validate(): string[] {
    const errs: string[] = [];
    if (!form.title.trim()) errs.push("Title is required");
    if (!form.kind) errs.push("Kind is required");
    if (!form.locale) errs.push("Locale is required");
    return errs;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setServerError(null);
    const errs = validate();
    if (errs.length > 0) {
      setErrors(errs);
      return;
    }
    setErrors([]);
    setSubmitting(true);

    try {
      const body = formToBody(form);
      if (isCreate) {
        await adminCreate(body);
      } else {
        await adminUpdate(id, body);
      }
      navigate("/admin");
    } catch (err: unknown) {
      setServerError(err instanceof Error ? err.message : "Server error");
      setSubmitting(false);
    }
  }

  const fieldClass = "w-full rounded-xl px-3 py-2 text-sm border outline-none focus:ring-2";
  const fieldStyle = {
    background: "var(--tg-secondary-bg)",
    color: "var(--tg-text)",
    borderColor: "transparent",
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: "var(--tg-bg)" }}>
        <BackBar to="/admin" />
        <p className="px-4 py-8" style={{ color: "var(--tg-hint)" }}>{t("loading")}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8" style={{ background: "var(--tg-bg)" }}>
      <BackBar to="/admin" />
      <div className="px-4">
        <h1 className="text-xl font-bold mb-6" style={{ color: "var(--tg-text)" }}>
          {isCreate ? t("admin_new") : t("admin_edit")}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Kind */}
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: "var(--tg-hint)" }}>Kind</label>
            <select
              className={fieldClass}
              style={fieldStyle}
              value={form.kind}
              onChange={(e) => set("kind", e.target.value as KindType)}
            >
              {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>

          {/* Locale */}
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: "var(--tg-hint)" }}>Locale</label>
            <select
              className={fieldClass}
              style={fieldStyle}
              value={form.locale}
              onChange={(e) => set("locale", e.target.value as LocaleType)}
            >
              {LOCALES.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: "var(--tg-hint)" }}>Title *</label>
            <input
              className={fieldClass}
              style={fieldStyle}
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="Title"
            />
          </div>

          {/* Subtitle */}
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: "var(--tg-hint)" }}>Subtitle</label>
            <input
              className={fieldClass}
              style={fieldStyle}
              value={form.subtitle}
              onChange={(e) => set("subtitle", e.target.value)}
              placeholder="Subtitle (optional)"
            />
          </div>

          {/* Paragraphs */}
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: "var(--tg-hint)" }}>
              Paragraphs (blank line = new paragraph)
            </label>
            <textarea
              className={fieldClass}
              style={{ ...fieldStyle, minHeight: "160px", resize: "vertical" }}
              value={form.paragraphsText}
              onChange={(e) => set("paragraphsText", e.target.value)}
              placeholder={"Paragraph one\n\nParagraph two"}
            />
          </div>

          {/* Image URL */}
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: "var(--tg-hint)" }}>Image URL</label>
            <input
              className={fieldClass}
              style={fieldStyle}
              value={form.image_url}
              onChange={(e) => set("image_url", e.target.value)}
              placeholder="https://..."
              type="url"
            />
          </div>

          {/* Link */}
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: "var(--tg-hint)" }}>Link</label>
            <input
              className={fieldClass}
              style={fieldStyle}
              value={form.link}
              onChange={(e) => set("link", e.target.value)}
              placeholder="https://..."
              type="url"
            />
          </div>

          {/* Date */}
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: "var(--tg-hint)" }}>Date</label>
            <input
              className={fieldClass}
              style={fieldStyle}
              value={form.date}
              onChange={(e) => set("date", e.target.value)}
              type="date"
            />
          </div>

          {/* Sort */}
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: "var(--tg-hint)" }}>Sort order</label>
            <input
              className={fieldClass}
              style={fieldStyle}
              value={form.sort}
              onChange={(e) => set("sort", e.target.value)}
              placeholder="0"
              type="number"
            />
          </div>

          {/* Published */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => set("published", e.target.checked)}
              className="w-5 h-5 rounded"
              style={{ accentColor: "var(--brand-blue)" }}
            />
            <span className="text-sm font-semibold" style={{ color: "var(--tg-text)" }}>
              {t("admin_publish")}
            </span>
          </label>

          {/* Validation errors */}
          {errors.length > 0 && (
            <div className="rounded-xl p-3" style={{ background: "#e5484d22" }}>
              {errors.map((e) => (
                <p key={e} className="text-sm" style={{ color: "var(--danger)" }}>{e}</p>
              ))}
            </div>
          )}

          {/* Server error */}
          {serverError && (
            <p className="text-sm" style={{ color: "var(--danger)" }}>{serverError}</p>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate("/admin")}
              className="flex-1 py-3 rounded-xl font-semibold text-sm"
              style={{ background: "var(--tg-secondary-bg)", border: "none", cursor: "pointer", color: "var(--tg-text)" }}
            >
              {t("admin_cancel")}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-3 rounded-xl font-semibold text-sm"
              style={{
                background: submitting ? "var(--surface-muted)" : "var(--brand-blue)",
                border: "none",
                cursor: submitting ? "not-allowed" : "pointer",
                color: submitting ? "var(--muted)" : "#ffffff",
              }}
            >
              {submitting ? t("loading") : t("admin_save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
